import { HttpClient, provideHttpClient } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withViewTransitions } from '@angular/router';
import { createErrorHandler } from '@sentry/angular';
import { getAI, VertexAIBackend } from 'firebase/ai';
import { getApp, initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import localeNL from '@angular/common/locales/nl';
import localeNO from '@angular/common/locales/no';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import {
  AuthService,
  FIREBASE_AI,
  FIREBASE_AUTH,
  FIREBASE_FIRESTORE,
  FIREBASE_FUNCTIONS,
  FIREBASE_STORAGE,
  initializeAuth,
  MatPaginatorIntlProvider,
} from '@verify/shared-components/services';
import { nl } from 'date-fns/locale';
import { DragToSelectModule } from 'ngx-drag-to-select';
import { environment } from '../environments/environment';
import { routes } from './app.routes';

registerLocaleData(localeNL, 'nl');
registerLocaleData(localeNO, 'no');

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

const app = initializeApp(environment.firebaseConfig);
if (environment.reCaptchaSiteKey) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(environment.reCaptchaSiteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useValue: createErrorHandler() },
    provideRouter(routes, withViewTransitions()),
    provideAnimations(),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
    ),
    {
      provide: FIREBASE_AUTH,
      useFactory: () => {
        const auth = getAuth();
        if (environment.useEmulators) {
          connectAuthEmulator(auth, 'http://localhost:9099', {
            disableWarnings: true,
          });
        }
        return auth;
      },
    },
    {
      provide: FIREBASE_FUNCTIONS,
      useFactory: () => {
        const functions = getFunctions(getApp(), environment.functionRegion);
        if (environment.useEmulators) {
          connectFunctionsEmulator(functions, 'localhost', 5001);
        }
        return functions;
      },
    },
    {
      provide: FIREBASE_FIRESTORE,
      useFactory: () => {
        const firestore = getFirestore(getApp(), environment.database);
        if (environment.useEmulators) {
          connectFirestoreEmulator(firestore, 'localhost', 8080);
        }
        return firestore;
      },
    },
    {
      provide: FIREBASE_STORAGE,
      useFactory: () => {
        const storage = getStorage();
        if (environment.useEmulators) {
          connectStorageEmulator(storage, 'localhost', 9199);
        }
        return storage;
      },
    },
    {
      provide: FIREBASE_AI,
      useFactory: () =>
        getAI(getApp(), {
          backend: new VertexAIBackend('global'),
          useLimitedUseAppCheckTokens: true,
        }),
    },
    importProvidersFrom(DragToSelectModule.forRoot()),
    provideAnimationsAsync(),
    provideAppInitializer(() => initializeAuth(inject(AuthService))),
    { provide: MAT_DATE_LOCALE, useValue: nl },
    { provide: LOCALE_ID, useValue: environment.localeId },
    provideDateFnsAdapter(),
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: MatPaginatorIntl,
      useClass: MatPaginatorIntlProvider,
    },
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
  ],
};
